// Route table (browser paths, same names as the mockup hash routes; #/settings → /me and #/admin/system →
// /admin/collection keep working as redirects). Screens live in src/screens/<name>/ and load on demand.
// handle = { menuId, titleKey, scopePerm?, perm? } — see shell/view.ts RouteHandle.
import { lazy } from 'react';
import { Navigate, useLocation, type RouteObject } from 'react-router';
import { LoginScreen } from './screens/login';
import { AppShell, ScreenError } from './shell/AppShell';
import { BareLayout, RequireAuth, RootLayout } from './shell/Gates';
import { StateScreen } from './shell/StateScreen';
import type { RouteHandle } from './shell/view';

const Home = lazy(() => import('./screens/home').then((m) => ({ default: m.HomeScreen })));
const Logs = lazy(() => import('./screens/logs').then((m) => ({ default: m.LogsScreen })));
const LogDetail = lazy(() => import('./screens/logs').then((m) => ({ default: m.LogDetailScreen })));
const Stats = lazy(() => import('./screens/stats').then((m) => ({ default: m.StatsScreen })));
const People = lazy(() => import('./screens/people').then((m) => ({ default: m.PeopleScreen })));
const Person = lazy(() => import('./screens/people').then((m) => ({ default: m.PersonScreen })));
const Team = lazy(() => import('./screens/people').then((m) => ({ default: m.TeamScreen })));
const Connect = lazy(() => import('./screens/connect').then((m) => ({ default: m.ConnectScreen })));
const ClientApproval = lazy(() => import('./screens/connect/ClientApprovalScreen').then(m => ({ default: m.ClientApprovalScreen })));
const Glossary = lazy(() => import('./screens/glossary').then((m) => ({ default: m.GlossaryScreen })));
const Term = lazy(() => import('./screens/glossary').then((m) => ({ default: m.TermScreen })));
const Me = lazy(() => import('./screens/me').then((m) => ({ default: m.MeScreen })));
const Roles = lazy(() => import('./screens/admin').then((m) => ({ default: m.RolesScreen })));
const Policy = lazy(() => import('./screens/admin').then((m) => ({ default: m.PolicyScreen })));
const Audit = lazy(() => import('./screens/admin').then((m) => ({ default: m.AuditScreen })));
const Collection = lazy(() => import('./screens/admin').then((m) => ({ default: m.CollectionScreen })));
const Cost = lazy(() => import('./screens/admin').then((m) => ({ default: m.CostScreen })));

/** Old/mockup path → new path, keeping the query (tab, item …). */
function RedirectKeepQuery({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={to + location.search} replace />;
}

const h = (handle: RouteHandle) => handle;

export const appRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: <BareLayout />,
        children: [{ index: true, element: <LoginScreen />, handle: h({ titleKey: 'nav.login' }) }],
      },
      {
        path: '/',
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                // screen errors are drawn inside the shell (the menu stays usable)
                errorElement: <ScreenError />,
                children: [
                  { index: true, element: <Navigate to="/home" replace /> },
                  { path: 'home', element: <Home />, handle: h({ menuId: 'home', titleKey: 'nav.home' }) },
                  { path: 'logs', element: <Logs />, handle: h({ menuId: 'logs', titleKey: 'nav.logs', scopePerm: 'logs.list' }) },
                  { path: 'logs/:id', element: <LogDetail />, handle: h({ menuId: 'logs', titleKey: 'logs.detail.page', scopePerm: 'logs.list' }) },
                  { path: 'stats', element: <Stats />, handle: h({ menuId: 'stats', titleKey: 'nav.stats', scopePerm: 'stats.view' }) },
                  { path: 'people', element: <People />, handle: h({ menuId: 'people', titleKey: 'nav.people' }) },
                  { path: 'people/:id', element: <Person />, handle: h({ menuId: 'people', titleKey: 'people.d.navTitle', perm: 'people.individual' }) },
                  { path: 'teams/:id', element: <Team />, handle: h({ menuId: 'people', titleKey: 'nav.people' }) },
                  { path: 'connect', element: <Connect />, handle: h({ menuId: 'connect', titleKey: 'nav.connect' }) },
                  { path: 'connect/approve', element: <ClientApproval />, handle: h({ menuId: 'connect', titleKey: 'connect.approval.title' }) },
                  { path: 'glossary', element: <Glossary />, handle: h({ menuId: 'glossary', titleKey: 'nav.glossary' }) },
                  { path: 'glossary/:slug', element: <Term />, handle: h({ menuId: 'glossary', titleKey: 'nav.glossary' }) },
                  { path: 'me', element: <Me />, handle: h({ menuId: 'me', titleKey: 'nav.settings' }) },
                  { path: 'admin/roles', element: <Roles />, handle: h({ menuId: 'admin/roles', titleKey: 'nav.admin.roles' }) },
                  { path: 'admin/policy', element: <Policy />, handle: h({ menuId: 'admin/policy', titleKey: 'nav.admin.policy' }) },
                  { path: 'admin/audit', element: <Audit />, handle: h({ menuId: 'admin/audit', titleKey: 'nav.admin.audit' }) },
                  { path: 'admin/collection', element: <Collection />, handle: h({ menuId: 'admin/collection', titleKey: 'nav.admin.system' }) },
                  { path: 'admin/cost', element: <Cost />, handle: h({ menuId: 'admin/cost', titleKey: 'nav.admin.cost' }) },
                  { path: 'settings', element: <RedirectKeepQuery to="/me" /> },
                  { path: 'admin/system', element: <RedirectKeepQuery to="/admin/collection" /> },
                  { path: '*', element: <StateScreen kind="notFound" />, handle: h({ titleKey: 'state.notFound.page' }) },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
